import React from 'react';
import { useFormikContext, Field, Formik } from 'formik';
import {
  Box,
  Checkbox,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Button from '@mui/lab/LoadingButton';
import { v4 } from 'uuid';
import format from 'date-fns/format';
import { ptBR } from 'date-fns/locale';
import * as yup from 'yup';
import { DateRange } from 'react-date-range';
import { useDispatch, useSelector } from 'react-redux';
import { flow, isEqual } from 'lodash';
import { Paper } from '@mui/material';
import DatePicker from 'react-datepicker';

import fetch from '@giro/shared-utils/fetch.util';

import useAuthHook from '@giro/shared-hooks/useAuth.hook';

import ScreenLayout from '@giro-pdv/layouts/Screen.layout';

import TextFieldLabel from '@giro-pdv/components/TextFieldLabel.component';

import FieldLabelFormik from '@giro-pdv/components/FieldLabelFormik.component';

import transactions from '../store/reportsSlips';

import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

import useFetchHook from '@giro-pdv/hooks/useFetch.hook';
import moment from 'moment';
import reports from '../store/reportsSlips';
import dialogDetailsSlips from '../store/dialogs/dialogDetailsSlips';

const months = [
  'JANEIRO',
  'FEVEREIRO',
  'MARÇO',
  'ABRIL',
  'MAIO',
  'JUNHO',
  'JULHO',
  'AGOSTO',
  'SETEMBRO',
  'OUTUBRO',
  'NOVEMBRO',
  'DEZEMBRO',
];

const FormSearchTransactions = ({ handleResetFilters, loading }) => {
  const { user } = useAuthHook();

  const [showModalRange, setModalRange] = React.useState(false);
  const { submitForm, errors, setFieldValue, values }: any = useFormikContext();
  const [key, setKey] = React.useState(v4());

  return (
    <>
      <Paper sx={{ p: 2 }}>
        <Box display="flex" flexDirection="column" gap={4}>
          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
            gap={4}
            key={key}
          >
            <FieldLabelFormik
              name="date"
              select
              size="small"
              label="Data"
              SelectProps={{
                native: true,
              }}
            >
              {months.map((i, idx) => {
                return (
                  <option value={idx} key={idx}>
                    {i}
                  </option>
                );
              })}
            </FieldLabelFormik>
          </Box>

          <Box alignSelf="flex-end" gap={2} display="flex" flexDirection="row">
            <Button onClick={handleResetFilters} disabled={loading}>
              Limpar filtros
            </Button>
            <Button
              variant="contained"
              onClick={submitForm}
              disabled={Object.keys(errors).length > 0 || loading}
            >
              Pesquisar
            </Button>
          </Box>
        </Box>
      </Paper>

      <Dialog open={showModalRange} onClose={() => setModalRange(false)}>
        <DateRange
          weekdayDisplayFormat="EEEEEE"
          monthDisplayFormat="MMMM yyyy"
          locale={ptBR}
          showDateDisplay={false}
          months={2}
          direction="horizontal"
          ranges={[
            {
              startDate: values.dateStart || new Date(),
              endDate: values.dateEnd || new Date(),
              key: 'selection',
            },
          ]}
          onChange={({ selection }) => {
            setFieldValue('dateStart', selection.startDate);
            setFieldValue('dateEnd', selection.endDate);

            setKey(v4());
          }}
        />
      </Dialog>
    </>
  );
};

const FormSearchTransactionsComponent = () => {
  const { user } = useAuthHook();

  const [key, setKey] = React.useState(v4());

  const validationSchema = yup.object({
    startDate: yup.date().nullable(),
    endDate: yup.date().nullable(),
  });

  const dispatch = useDispatch();

  const dispatchRedux = {
    updateFilters: flow(transactions.action.updateFilters, dispatch),
    resetFilters: flow(transactions.action.resetFilters, dispatch),
  };

  const selectorRedux = {
    filters: useSelector(transactions.selector.selectFilters),
    loading: useSelector(transactions.selector.selectLoading),
  };

  const handleUpdateFilters = async (values) => {
    dispatchRedux.updateFilters(values);

    return true;
  };

  const handleResetFilters = () => {
    dispatchRedux.resetFilters();

    if (!isEqual(selectorRedux.filters, transactions.initialState.filters)) {
      dispatchRedux.updateFilters({
        type: 'ec',
      });
    }

    setKey(v4());
  };

  const isBlockedSelect = user?.user?.codes?.idRole === 6;

  return (
    <Formik
      key={key}
      initialValues={{
        ...selectorRedux.filters,
        userCode: !isBlockedSelect ? undefined : user?.user?.codes?.idUser,
      }}
      enableReinitialize
      onSubmit={handleUpdateFilters}
      validationSchema={validationSchema}
      validateOnMount
    >
      <FormSearchTransactions
        handleResetFilters={handleResetFilters}
        loading={selectorRedux.loading}
      />
    </Formik>
  );
};

const TransactionsScreen = () => {
  const selectorRedux = {
    filters: useSelector(transactions.selector.selectFilters),
    data: useSelector(reports.selector.selectData),
    loading: useSelector(reports.selector.selectLoading),
  };

  const dispatch = useDispatch();

  const dispatchRedux = {
    updateFilters: flow(reports.action.updateFilters, dispatch),
    resetState: flow(reports.action.resetState, dispatch),
    receiptOpen: flow(dialogDetailsSlips.action.open, dispatch),
  };

  React.useEffect(() => {
    dispatchRedux.updateFilters({
      type: 'ec',
    });

    return () => {
      dispatchRedux.resetState();
    };
  }, []);
  const formatToCurrency = (number) =>
    Number(number).toLocaleString('pt-br', {
      style: 'currency',
      currency: 'BRL',
    });

  return (
    <ScreenLayout title="Relatório de boletos">
      <Box display="flex" flexDirection="column" gap={4}>
        <FormSearchTransactionsComponent />
      </Box>
      <Box display="flex" flexDirection="column" gap={4}>
        {selectorRedux.data?.length > 0 && (
          <TableContainer component={Paper}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 10000 }}>
                  <Typography
                    variant="trasso_heading_medium"
                    color="trasso.purple.100"
                  >
                    {months[selectorRedux.filters.date]}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <Table sx={{ minWidth: 1000 }} aria-label="simple table">
              <TableBody>
                {selectorRedux.data.map((row, i) => {
                  return (
                    <TableRow
                      key={i}
                      sx={{
                        cursor: 'pointer',
                        '&:nth-of-type(odd)': {
                          backgroundColor: 'trasso.gray.20',
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor: 'white',
                        },
                        '&:last-child td, &:last-child th': {
                          border: 0,
                        },
                      }}
                    >
                      {row.map((cell, index) => {
                        const splitCell = cell.date.split(',');
                        if (i === 0 && index === 0) {
                          const weekday = moment(splitCell[0]).weekday();
                          const array = Array(weekday > 0 ? weekday : 7).fill(
                            0
                          );
                          return array.map((r, idx) => {
                            if (idx === weekday - 1 || idx === 6) {
                              return (
                                <TableCell
                                  sx={{
                                    cursor: 'pointer',
                                    '&:nth-of-type(odd)': {
                                      backgroundColor: 'trasso.gray.20',
                                    },
                                    '&:nth-of-type(even)': {
                                      backgroundColor: 'white',
                                    },
                                    '&:last-child td, &:last-child th': {
                                      border: 0,
                                    },
                                  }}
                                  align="left"
                                  style={{
                                    width: 35,
                                    height: 150,
                                    verticalAlign: 'top',
                                  }}
                                  key={idx}
                                >
                                  {(idx === weekday - 1 || idx === 6) && (
                                    <Box display="flex" flexDirection="column">
                                      <Typography
                                        variant="trasso_heading_small"
                                        color="trasso.purple.100"
                                      >
                                        {splitCell[2].toUpperCase()}{' '}
                                        {splitCell[1]}
                                      </Typography>
                                      <Typography
                                        variant="trasso_heading_small"
                                        color="trasso.purple.80"
                                      >
                                        Emitido: {cell?.data?.count || 0}
                                      </Typography>
                                      <Typography
                                        variant="trasso_heading_small"
                                        color="trasso.purple.80"
                                      >
                                        {formatToCurrency(
                                          cell?.data?.total_amount || 0
                                        )}
                                      </Typography>
                                    </Box>
                                  )}
                                </TableCell>
                              );
                            } else {
                              return (
                                <TableCell
                                  sx={{
                                    backgroundColor: 'white',
                                  }}
                                  align="left"
                                  style={{
                                    width: 35,
                                    height: 150,
                                    verticalAlign: 'top',
                                  }}
                                  key={idx}
                                ></TableCell>
                              );
                            }
                          });
                        }

                        return (
                          <TableCell
                            sx={{
                              cursor: 'pointer',
                              '&:nth-of-type(odd)': {
                                backgroundColor: 'trasso.gray.20',
                              },
                              '&:nth-of-type(even)': {
                                backgroundColor: 'white',
                              },
                              '&:last-child td, &:last-child th': {
                                border: 1,
                              },
                              '&:hover': {
                                backgroundColor: 'trasso.gray.30',
                              },
                            }}
                            align="left"
                            style={{
                              width: 35,
                              height: 150,
                              verticalAlign: 'top',
                            }}
                            onClick={() => {
                              dispatchRedux.receiptOpen(cell?.date);
                            }}
                            key={cell}
                          >
                            <Box display="flex" flexDirection="column">
                              <Typography
                                variant="trasso_heading_small"
                                color="trasso.purple.100"
                              >
                                {splitCell[2].toUpperCase()} {splitCell[1]}
                              </Typography>
                              <Typography
                                variant="trasso_heading_small"
                                color="trasso.purple.80"
                              >
                                Emitido: {cell?.data?.count || 0}
                              </Typography>
                              <Typography
                                variant="trasso_heading_small"
                                color="trasso.purple.80"
                              >
                                {formatToCurrency(
                                  cell?.data?.total_amount || 0
                                )}
                              </Typography>
                            </Box>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </ScreenLayout>
  );
};

export default TransactionsScreen;
